import { create } from 'zustand';
import { FlattenedUserPermissions } from '../types/rolePermissions.interface';
import { getFromLocalStorage, removeFromLocalStorage, setInLocalStorage } from 'utils/localStorage/localStorage.utils';

interface PermissionsStore {
  permissions: FlattenedUserPermissions | null;
  // eslint-disable-next-line no-unused-vars
  setPermissions: (perms: FlattenedUserPermissions | null) => void;
  clearPermissions: () => void;
  loadFromLocalStorage: () => void;
  // eslint-disable-next-line no-unused-vars
  hasPermission: (pageCode: string, permissionCode: string) => boolean;
  // eslint-disable-next-line no-unused-vars
  getPagePermissions: (pageCode: string) => Record<string, boolean> | null;
  // eslint-disable-next-line no-unused-vars
  isPageAccessible: (pageCode: string) => boolean;
}

/**
 * Zustand store para gestionar permisos del usuario
 * Centraliza el acceso a permisos en toda la aplicación
 */
export const usePermissionsStore = create<PermissionsStore>((set, get) => ({
  permissions: null,

  /**
   * Establece los permisos y los guarda en localStorage
   */
  setPermissions: (perms: FlattenedUserPermissions | null) => {
    set({ permissions: perms });
    if (perms) {
      setInLocalStorage('userPermissions', perms);
    } else {
      removeFromLocalStorage('userPermissions');
    }
  },

  /**
   * Limpia los permisos del estado y localStorage
   */
  clearPermissions: () => {
    set({ permissions: null });
    removeFromLocalStorage('userPermissions');
  },

  /**
   * Carga los permisos desde localStorage al iniciar la app
   */
  loadFromLocalStorage: () => {
    const userPerms = getFromLocalStorage<FlattenedUserPermissions>('userPermissions');
    if (userPerms) {
      try {
        set({ permissions: userPerms });
      } catch (error) {
        console.error('Error parsing stored permissions:', error);
        removeFromLocalStorage('userPermissions');
      }
    }
  },

  /**
   * Verifica si el usuario tiene un permiso específico
   * @param pageCode - Código de la página (ej: 'user', 'dashboard')
   * @param permissionCode - Código del permiso (ej: 'create-user')
   * @returns true si tiene el permiso, false en caso contrario
   */
  hasPermission: (pageCode: string, permissionCode: string): boolean => {
    const { permissions } = get();
    if (!permissions) {
      return false;
    }
    
    const pagePerms = permissions.pagePermissions[pageCode];
    if (!pagePerms) {
      return false;
    }
    const result = pagePerms.permissions[permissionCode] ?? false;
    return result;
  },

  /**
   * Obtiene todos los permisos de una página específica
   * @param pageCode - Código de la página
   * @returns Objeto con todos los permisos de la página o null
   */
  getPagePermissions: (pageCode: string): Record<string, boolean> | null => {
    const { permissions } = get();
    if (!permissions) return null;
    
    const pagePerms = permissions.pagePermissions[pageCode];
    return pagePerms?.permissions ?? null;
  },

  /**
   * Verifica si el usuario tiene acceso a una página (permiso de acceso)
   * @param pageCode - Código de la página en singular (ej: 'user', 'provider')
   * @returns true si tiene acceso, false en caso contrario
   */
  isPageAccessible: (pageCode: string): boolean => {
    const { hasPermission } = get();
    // Mapeo de pageCode singular a permiso de acceso plural
    const accessPermissionMap: Record<string, string> = {
      'dashboard': 'access-dashboard',
      'user': 'access-users',
      'roles': 'access-roles',
      'provider': 'access-providers',
      'provider-category': 'access-provider-categories',
      'purchases': 'access-purchases',
      'cost-center': 'access-cost-centers',
      'chapter': 'access-chapters',
      'employee': 'access-employees',
      'payroll-sheet': 'access-payroll-sheets',
      'overtime': 'access-overtimes',
      'inventory': 'access-inventories',
      'asset-category': 'access-asset-categories',
      'asset': 'access-assets',
      'asset-assignment': 'access-asset-assignments',
      'consumable': 'access-consumables',
      'report': 'access-reports',
      'kiosko': 'access-kiosko',
      'kiosko-assignment': 'access-assignment-kiosko',
    };
    const accessPermission = accessPermissionMap[pageCode] || `access-${pageCode}s`;
    return hasPermission(pageCode, accessPermission);
  },
}));
